import { DocumentReference } from "firebase/firestore";

import { brokerModel } from "../models/BrokerModel";
import { DataHelpers } from "@/core/modules/helpers/DataHelpers";
import { FirestoreDocument } from "@/core/modules/firestore/objects/FirestoreDocument";
import { Referent } from "@/features/modules/referent/objects/Referent";

import { AddressField, ArrayField, BooleanField, DateField, StringField } from "@/core/fields";

export class Broker extends FirestoreDocument {
  public name: string | undefined = undefined;
  public address: AddressField = new AddressField();
  public phone: string | undefined = undefined;
  public legalRepresentative: string | undefined = undefined;
  public vatCode: string | undefined = undefined;
  public fiscalCode: string | undefined = undefined;
  public recipientCode: string | undefined = undefined;
  public communicationEmail: string | undefined = undefined;
  public certifiedEmail: string | undefined = undefined;
  public companyReferents: Referent[] = [];
  public notes: string | undefined = undefined;
  public archived = false;
  public archivedDate: Date | undefined = undefined;
  public trackExpirations = true;

  public constructor(firestoreData?: Record<string, unknown>, id?: string) {
    super(id);
    if (firestoreData !== undefined) this.fromFirestore(firestoreData, id);
  }

  public fromFirestore(data: Record<string, unknown>, id?: string, firestoreRef?: DocumentReference): Broker {
    super.fromFirestore(data, id, firestoreRef);

    this.name = StringField.fromFirestore(data.name);
    this.address.fromFirestore(data.address);
    this.phone = StringField.fromFirestore(data.phone);
    this.legalRepresentative = StringField.fromFirestore(data.legalRepresentative);
    this.vatCode = StringField.fromFirestore(data.vatCode);
    this.fiscalCode = StringField.fromFirestore(data.fiscalCode);
    this.recipientCode = StringField.fromFirestore(data.recipientCode);
    this.communicationEmail = StringField.fromFirestore(data.communicationEmail);
    this.certifiedEmail = StringField.fromFirestore(data.certifiedEmail);
    this.companyReferents = ArrayField.fromFirestore<Referent>(data.companyReferents, (value) => new Referent(value));
    this.notes = StringField.fromFirestore(data.notes);
    this.archived = BooleanField.fromFirestore(data.archived);
    this.archivedDate = DateField.fromFirestore(data.archivedDate);
    this.trackExpirations = BooleanField.fromFirestore(data.trackExpirations);

    return this;
  }

  public toFirestore(): Record<string, unknown> {
    const firestoreData: Record<string, unknown> = super.toFirestore();

    firestoreData.name = StringField.toFirestore(this.name);
    firestoreData.address = this.address.toFirestore();
    firestoreData.phone = StringField.toFirestore(this.phone);
    firestoreData.legalRepresentative = StringField.toFirestore(this.legalRepresentative);
    firestoreData.vatCode = StringField.toFirestore(this.vatCode);
    firestoreData.fiscalCode = StringField.toFirestore(this.fiscalCode);
    firestoreData.recipientCode = StringField.toFirestore(this.recipientCode);
    firestoreData.communicationEmail = StringField.toFirestore(this.communicationEmail);
    firestoreData.certifiedEmail = StringField.toFirestore(this.certifiedEmail);
    firestoreData.companyReferents = ArrayField.toFirestore<Referent>(this.companyReferents, (value) => value.toFirestore());
    firestoreData.notes = StringField.toFirestore(this.notes);
    firestoreData.archived = BooleanField.toFirestore(this.archived);
    firestoreData.archivedDate = DateField.toFirestore(this.archivedDate);
    firestoreData.trackExpirations = BooleanField.toFirestore(this.trackExpirations);

    return firestoreData;
  }

  public setSearchKeys(): void {
    this.searchKeys = DataHelpers.createSearchKeys(this.name);
    if (this.vatCode !== undefined) this.searchKeys.push(this.vatCode.toLowerCase());
    if (this.fiscalCode !== undefined) this.searchKeys.push(this.fiscalCode.toLowerCase());
  }

  public async handleArchive(): Promise<void> {
    if (this.id === "new") {
      this.archivedDate = this.archived === true ? new Date() : undefined;
    } else {
      const oldBroker: Broker = await brokerModel.getDocument(this.id);
      if (oldBroker.archived !== this.archived) {
        this.archivedDate = this.archived === true ? new Date() : undefined;
      }
    }
  }
}
